import { RouteObject, createBrowserRouter, Navigate } from 'react-router-dom';
import Ecommerce from 'pages/dashboard/ecommerce';
import EcommerceLayout from 'layouts/EcommerceLayout';
import Homepage from 'pages/apps/e-commerce/customer/Homepage';
import ProductDetails from 'pages/apps/e-commerce/customer/ProductDetails';
import Cart from 'pages/apps/e-commerce/customer/Cart';
import Checkout from 'pages/apps/e-commerce/customer/checkout/Checkout';
import ProductsFilter from 'pages/apps/e-commerce/customer/ProductsFilter';
import ShippingInfo from 'pages/apps/e-commerce/customer/checkout/ShippingInfo';
import Profile from 'pages/apps/e-commerce/customer/Profile';
import VendorDetailPage from 'pages/apps/e-commerce/customer/VendorDetailPage';
import Wishlist from 'pages/apps/e-commerce/customer/Wishlist';
import VendorPage from 'pages/apps/e-commerce/customer/VendorPage';
import Invoice from 'pages/apps/e-commerce/customer/Invoice';
import OrderTracking from 'pages/apps/e-commerce/customer/OrderTracking';
import AddProduct from 'pages/apps/e-commerce/admin/AddProduct';
import OrderDetails from 'pages/apps/e-commerce/admin/OrderDetails';
import Error404 from 'pages/error/Error404';
import Error403 from 'pages/error/Error403';
import Error500 from 'pages/error/Error500';
import App from 'App';
import CardSignIn from 'pages/pages/authentication/card/SignIn';
import CardSignUp from 'pages/pages/authentication/card/SignUp';
import CardForgotPassword from 'pages/pages/authentication/card/ForgotPassword';
import CardSignOut from 'pages/pages/authentication/card/SignOut';
import CardResetPassword from 'pages/pages/authentication/card/ResetPassword';
import CardTwoFA from 'pages/pages/authentication/card/TwoFA';
import CardLockScreen from 'pages/pages/authentication/card/LockScreen';
import { Suspense, lazy } from 'react';
import PhoenixLoader from 'components/common/PhoenixLoader';
import ProtectedRoutes from 'routers/ProtectedRoutes';
import PublicRoutes from 'routers/PublicRoute';
import VendorRoutes from 'routers/VendorRoutes';
import MyOrders from 'pages/apps/e-commerce/customer/MyOrders';
import CustomerProducts from 'pages/apps/e-commerce/customer/CustomerProducts';
import VendorProducts from 'pages/apps/e-commerce/customer/VendorProducts';
import BrandsPage from 'pages/apps/e-commerce/customer/BrandsPage';
import BrandDetailPage from 'pages/apps/e-commerce/customer/BrandDetailPage';
import VisitorTracker from 'helpers/VisitorTracker';
import CategoriesPage from 'pages/apps/e-commerce/customer/CategoriesPage';
import CategoryTypePage from 'pages/apps/e-commerce/customer/CategoryTypePage';
import VendorCoupons from 'pages/apps/e-commerce/customer/VendorCoupons';
import AddCoupon from 'pages/apps/e-commerce/admin/couponpages/AddCoupon';
import EditCoupon from 'pages/apps/e-commerce/admin/couponpages/EditCoupon';

const EditProduct = lazy(
  () => import('pages/apps/e-commerce/admin/EditProduct')
);

const routes: RouteObject[] = [
  {
    element: <App />,
    children: [
      {
        path: '/',
        element: <EcommerceLayout />,
        children: [
          {
            path: '/',
            element: (
              <VisitorTracker>
                <Homepage />
              </VisitorTracker>
            )
          },
          {
            path: '/dashboard',
            children: [
              {
                path: 'e-commerce',
                element: (
                  <ProtectedRoutes>
                    <VisitorTracker>
                      <Ecommerce />
                    </VisitorTracker>
                  </ProtectedRoutes>
                )
              }
            ]
          }
        ]
      },
      {
        element: <EcommerceLayout />,
        path: '/',
        children: [
          {
            path: 'product-details/:slug',
            element: (
              <VisitorTracker>
                <ProductDetails />
              </VisitorTracker>
            )
          },
          {
            path: 'cart',
            element: <Cart />
          },
          {
            path: 'checkout/:vendorId',
            element: (
              <ProtectedRoutes>
                <Checkout />
              </ProtectedRoutes>
            )
          },
          {
            path: 'shipping-info',
            element: <ShippingInfo />
          },
          {
            path: 'profile',
            element: (
              <VisitorTracker>
                <Profile />
              </VisitorTracker>
            )
          },
          {
            path: 'vendordetails/:id',
            element: (
              <VisitorTracker>
                <VendorDetailPage />
              </VisitorTracker>
            )
          },
          {
            path: 'branddetails/:slug',
            element: (
              <VisitorTracker>
                <BrandDetailPage />
              </VisitorTracker>
            )
          },
          {
            path: 'products-filter',
            element: (
              <VisitorTracker>
                <ProductsFilter />
              </VisitorTracker>
            )
          },
          {
            path: 'wishlist',
            element: (
              <ProtectedRoutes>
                <Wishlist />
              </ProtectedRoutes>
            )
          },
          {
            path: 'vendors',
            element: (
              <VisitorTracker>
                <VendorPage />
              </VisitorTracker>
            )
          },
          {
            path: 'brands',
            element: (
              <VisitorTracker>
                <BrandsPage />
              </VisitorTracker>
            )
          },
          {
            path: 'all-categories',
            element: (
              <VisitorTracker>
                <CategoriesPage />
              </VisitorTracker>
            )
          },
          {
            path: 'store/:slug',
            element: (
              <VisitorTracker>
                <CategoryTypePage />
              </VisitorTracker>
            )
          },
          {
            path: 'order-tracking',
            element: (
              <ProtectedRoutes>
                <OrderTracking />
              </ProtectedRoutes>
            )
          },
          {
            path: 'invoice',
            element: (
              <ProtectedRoutes>
                <Invoice />
              </ProtectedRoutes>
            )
          },
          {
            path: 'my-orders',
            element: (
              <ProtectedRoutes>
                <MyOrders />
              </ProtectedRoutes>
            )
          },
          {
            path: 'order-details/:orderId',
            element: (
              <ProtectedRoutes>
                <OrderDetails />
              </ProtectedRoutes>
            )
          },
          {
            path: 'my-products',
            element: (
              <ProtectedRoutes>
                <CustomerProducts />
              </ProtectedRoutes>
            )
          },
          {
            path: 'vendor-products',
            element: (
              <ProtectedRoutes>
                <VendorProducts />
              </ProtectedRoutes>
            )
          },

          {
            path: 'add-product',
            element: (
              <VendorRoutes>
                <AddProduct />
              </VendorRoutes>
            )
          },
          {
            path: 'edit-product/:productId',
            element: (
              <VendorRoutes>
                <Suspense fallback={<PhoenixLoader />}>
                  <EditProduct />
                </Suspense>
              </VendorRoutes>
            )
          },
          // {
          //   path: 'vendor-coupons',
          //   element: (
          //     <ProtectedRoutes>
          //       <VendorCoupons />
          //     </ProtectedRoutes>
          //   )
          // },
          {
            path: 'add-coupon',
            element: (
              <ProtectedRoutes>
                <AddCoupon />
              </ProtectedRoutes>
            )
          },
          {
            path: 'edit-coupon/:couponId',
            element: (
              <ProtectedRoutes>
                <EditCoupon />
              </ProtectedRoutes>
            )
          }
        ]
      },
      {
        path: '/pages/errors/',
        children: [
          {
            path: '404',
            element: <Error404 />
          },
          {
            path: '403',
            element: <Error403 />
          },
          {
            path: '500',
            element: <Error500 />
          }
        ]
      },
      {
        path: '/',
        children: [
          {
            path: 'sign-in',
            element: (
              <PublicRoutes>
                <CardSignIn />
              </PublicRoutes>
            )
          },
          {
            path: 'sign-up',
            element: (
              <PublicRoutes>
                <CardSignUp />
              </PublicRoutes>
            )
          },
          {
            path: 'sign-out',
            element: <CardSignOut />
          },
          {
            path: 'forgot-password',
            element: (
              <PublicRoutes>
                <CardForgotPassword />
              </PublicRoutes>
            )
          },
          {
            path: 'reset-password',
            element: (
              <PublicRoutes>
                <CardResetPassword />
              </PublicRoutes>
            )
          },
          {
            path: 'lock-screen',
            element: <CardLockScreen />
          },
          {
            path: '2FA',
            element: (
              <PublicRoutes>
                <CardTwoFA />
              </PublicRoutes>
            )
          }
        ]
      },
      {
        path: '*',
        element: <Error404 />
      }
    ]
  }
];

export const router = createBrowserRouter(routes);

export default routes;
